/**
 * useRfpsList
 * React Query wrapper for the list RFP endpoint supporting submitted & draft states.
 * Query Key shape: [ 'rfps', 'submitted' | 'drafts', { page, limit, query, sortKey, statuses, brokerId } ]
 * - page: zero-based page index used in UI, API is converted to +1 internally
 * - query: trimmed & lowercased before request; only triggers fetch when length >= 3 or empty
 * - sortKey: already includes leading '-' when descending (mapping performed by caller)
 * - statuses: sorted array of status values (stable ordering for cache key)
 *
 * Optimistic Updates: Draft discard mutation filters cached 'drafts' query results.
 */

import { useApi } from "@platform/api";
import { useQuery, UseQueryResult, QueryClient } from "@tanstack/react-query";

import { RFPS_SUBMITTED, RFPS_TAB_MAPPING } from "../constants";
import { RfpSubmitter } from "../types/clientTab";
import { IAnalyst } from "../pages/RFPDetails/components/rfpInformationTypes";

export interface RfpsBaseParams {
  page: number; // zero-based
  limit: number;
  query: string;
  sortBy?: string; // UI sort key (mapped externally)
  sortKey?: string; // API-ready sort key with leading - when descending
  statusFilters: string[]; // camelCase status keys (keys of RFP_STATUSES)
}

export interface RfpListItem {
  /**
   * Primary identifier for the RFP. Backend responses currently return a number but
   * some existing UI code may treat ids as strings, so we allow both to avoid
   * the need for pervasive casts while we stabilize the contract.
   */
  id: number | string;

  // Commonly-used (subset) fields observed in draft & submitted responses. All optional
  // so we don't over‑constrain early iterations; refine as the feature hardens.
  brokerId?: number;
  brokerName?: string;
  clientName?: string;
  creationDate?: string; // ISO timestamp
  lastModifyDate?: string; // ISO timestamp
  dueDate?: string | null; // date string or null when not provided
  currentStep?: string; // e.g. "RFP Details", "Documents"
  status?: string; // e.g. underReview
  metaData?: {
    clientId?: number;
    clientName?: string;
    [k: string]: unknown;
  };
  client?: { id?: number; name?: string };
  products?: string[];
  submitter?: RfpSubmitter;
  creator?: RfpSubmitter;
  assignedAnalyst?: IAnalyst;
  details?: {
    carriers?: string[];
  };

  /**
   * Other backend-supplied properties intentionally left as unknown for now.
   * Replace ad-hoc usages with explicit fields here as they are required.
   */
  [key: string]: unknown;
}

export interface UseRfpsListParams extends RfpsBaseParams {
  rfpState: number; // RFPS_SUBMITTED or draft
  brokerId?: string | number;
  clientId?: string | number;
  enabled?: boolean;
}

// Backend listRfp response (union of draft & submitted minimal fields already normalized
// upstream for the table views). Add/ tighten fields as needed.
export interface RfpsListResponse {
  results: RfpListItem[];
  totalResults: number;
  totalPages?: number;
  page?: number;
  limit?: number;
  sort?: string;
  // Allow forward‑compatible metadata without loosening to any.
  [k: string]: unknown;
}

export const getRfpsListQueryKey = (params: UseRfpsListParams) =>
  [
    "rfps",
    params.rfpState === RFPS_SUBMITTED ? "submitted" : "drafts",
    {
      page: params.page,
      limit: params.limit,
      query: params.query,
      sortKey: params.sortKey,
      statuses: params.statusFilters.slice().sort(),
      brokerId: params.brokerId ?? null,
      clientId: params.clientId ?? null,
    },
  ] as const;

export async function fetchRfpsList(
  axios: ReturnType<typeof useApi>,
  params: UseRfpsListParams,
): Promise<RfpsListResponse> {
  const { rfpState, page, limit, query, sortKey, statusFilters, brokerId } =
    params;
  const encodedSearchQuery = encodeURIComponent(query.trim().toLowerCase());
  // statusFilters now contains camelCase keys directly, no mapping needed
  const activeKeys = statusFilters.filter(Boolean).join(",");
  const statusParam = activeKeys ? `&status=${activeKeys}` : "";
  const url = `/brokerportal/v1/listRfp?q=${encodedSearchQuery}&rfpState=${RFPS_TAB_MAPPING[rfpState]}&page=${
    page + 1
  }&limit=${limit}${sortKey ? `&sort=${sortKey}` : ""}${statusParam}${brokerId ? `&brokerId=${brokerId}` : ""}`;
  // Provide expected response type to axios; unknown enforces narrowing below.
  const { data } = await axios.get<RfpsListResponse>(url);
  // Defensive fallback shaping while retaining type safety.
  return {
    results: Array.isArray(data?.results) ? data.results : [],
    totalResults:
      typeof data?.totalResults === "number" ? data.totalResults : 0,
    totalPages:
      typeof data?.totalPages === "number" ? data.totalPages : undefined,
    page: typeof data?.page === "number" ? data.page : undefined,
    limit: typeof data?.limit === "number" ? data.limit : undefined,
    sort: typeof data?.sort === "string" ? data.sort : undefined,
  };
}

export function useRfpsList(
  params: UseRfpsListParams,
): UseQueryResult<RfpsListResponse, Error> {
  const { query, enabled = true } = params;
  const axios = useApi();
  return useQuery<RfpsListResponse, Error>({
    queryKey: getRfpsListQueryKey(params),
    enabled: enabled && (query.trim().length === 0 || query.trim().length >= 3),
    queryFn: () => fetchRfpsList(axios, params),
  });
}

// Prefetch helper for opposite tab (caller must supply QueryClient & fetch config)
export async function prefetchRfpsList(
  client: QueryClient,
  axios: ReturnType<typeof useApi>,
  params: UseRfpsListParams,
): Promise<void> {
  await client.prefetchQuery({
    queryKey: getRfpsListQueryKey(params),
    queryFn: () => fetchRfpsList(axios, params),
  });
}
